import {
    combineSlices,
    compose,
    configureStore,
    type ConfigureStoreOptions,
    type ThunkAction,
    type UnknownAction,
} from '@reduxjs/toolkit';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import persistState from 'redux-sessionstorage';

import { announcementApi } from 'store/api/announcementApi';
import { authApi } from 'store/api/authApi';
import { clinicalPolicyApi } from 'store/api/clinicalPolicyApi';
import { epaApi } from 'store/api/epaApi';
import { intakeCaseApi } from 'store/api/intakeCaseApi';
import { referenceApi } from 'store/api/referenceApi';
import { scopeAssessorApi } from 'store/api/scopeAssessorApi';
import { treatmentApi } from 'store/api/treatmentApi';
import { authSlice } from 'store/auth/authSlice';
import { gridsSlice } from 'store/grids/gridsSlice';
import { archivedIntakeCaseSlice } from 'store/intakeCases/archivedIntakeCaseSlice';
import { intakeFormSlice } from 'store/intakeForm/intakeFormSlice';
import { archivedIntakeSourceSlice } from 'store/intakeSources/archivedIntakeSourceSlice';

const sessionStorageEnhancer = compose(
    persistState([intakeFormSlice.name, gridsSlice.name], { key: 'lighthouseStore' }),
);

const slices = [
    archivedIntakeCaseSlice,
    archivedIntakeSourceSlice,
    authSlice,
    gridsSlice,
    intakeFormSlice,
];

const apiSlices = [
    announcementApi,
    authApi,
    clinicalPolicyApi,
    epaApi,
    intakeCaseApi,
    referenceApi,
    scopeAssessorApi,
    treatmentApi,
];

const rootReducer = combineSlices(...slices, ...apiSlices);

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false }).concat(
            ...apiSlices.map((slice) => slice.middleware),
        ),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sessionStorageEnhancer),
    devTools: process.env.REACT_APP_ENV !== 'prd',
});

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;

export type RootState = ReturnType<typeof store.getState>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    UnknownAction
>;

/** Used for configuration of redux store in tests */
export const MockStore: ConfigureStoreOptions = {
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false }).concat(
            ...apiSlices.map((slice) => slice.middleware),
        ),
};
